/**
 * FieldStateSegmented — trójstanowy wybór pola typu dokumentu „Nie dotyczy · Opcjonalne · Wymagane”.
 *
 * Wspólny dla panelu bocznego „Pola typu” (Ustawienia) i panelu ITE (`TypeFieldGatingPanel`).
 * Wzorzec ARIA radiogroup: `role="radiogroup"` + `role="radio"`, fokus przemieszczany (`tabIndex` 0
 * tylko na jednym segmencie), strzałki lewo/prawo (i góra/dół) przesuwają cyklicznie, Home/End skaczą
 * na skraj. Dwa tryby aktywacji:
 *  - `auto` (domyślny): strzałki od razu zmieniają wybór (panel ITE, zapis dopiero przyciskiem);
 *  - `manual`: strzałki/Home/End przesuwają tylko fokus, wybór dopiero Spacją/Enterem albo
 *    kliknięciem (panel klienta, gdzie wybór zapisuje się natychmiast — ruch strzałką nie może
 *    wysyłać zapisów).
 * Blokada trwała (`disabled`: brak prawa, pole numeracji) używa `aria-disabled` (nie `disabled`):
 * wybrany stan zachowuje kolor i nadal da się go sfokusować, a zmiana jest ignorowana. Zapis w toku
 * (`busy`) także ignoruje zmianę, ale nie wyszarza segmentów — sygnalizuje tylko `aria-busy`.
 *
 * Etykiety stanów pochodzą z jednego zestawu kluczy `tenant.documentTypes.fields.state.*`.
 */

import type { KeyboardEvent } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DOCUMENT_TYPE_FIELD_STATE_VALUES, type DocumentTypeFieldState } from '@saas/shared';

import { Button } from '@/components/Button';
import { cn } from '@/lib/utils';

const STATE_LABEL_KEY: Record<DocumentTypeFieldState, string> = {
  excluded: 'tenant.documentTypes.fields.state.excluded',
  optional: 'tenant.documentTypes.fields.state.optional',
  required: 'tenant.documentTypes.fields.state.required',
};

// Wybrany segment trzyma kolor także po najechaniu i wciśnięciu (`hover:`/`active:`).
const STATE_ACTIVE_CLASS: Record<DocumentTypeFieldState, string> = {
  excluded:
    'bg-bg-tertiary text-text-primary font-semibold hover:bg-bg-tertiary hover:text-text-primary active:bg-bg-tertiary active:text-text-primary',
  optional:
    'bg-accent-50 text-accent-700 font-semibold hover:bg-accent-50 hover:text-accent-700 active:bg-accent-50 active:text-accent-700',
  required:
    'bg-[rgb(var(--color-error-subtle))] text-[rgb(var(--color-error-text))] font-semibold hover:bg-[rgb(var(--color-error-subtle))] hover:text-[rgb(var(--color-error-text))] active:bg-[rgb(var(--color-error-subtle))] active:text-[rgb(var(--color-error-text))]',
};

export interface FieldStateSegmentedProps {
  value: DocumentTypeFieldState;
  onChange: (next: DocumentTypeFieldState) => void;
  /** Nazwa pola — etykieta dostępności grupy. */
  label: string;
  /** Trwała blokada zmiany (brak prawa, pole z numeracji); wybrany stan zostaje widoczny. */
  disabled?: boolean;
  /** Zapis w toku — zmiana ignorowana, bez wyszarzania (`aria-busy`). */
  busy?: boolean;
  /** `auto` = strzałka zmienia wybór; `manual` = strzałka przesuwa fokus, wybór Spacją/Enterem. */
  activation?: 'auto' | 'manual';
  /** Prefiks `data-testid` przycisków: `<prefix>-<stan>`. */
  testIdPrefix?: string;
}

export function FieldStateSegmented({
  value,
  onChange,
  label,
  disabled = false,
  busy = false,
  activation = 'auto',
  testIdPrefix,
}: FieldStateSegmentedProps) {
  const { t } = useTranslation();
  const buttons = useRef<Partial<Record<DocumentTypeFieldState, HTMLButtonElement | null>>>({});
  // Segment z fokusem w trybie ręcznym; obowiązuje tylko dopóki `value` się nie zmieni.
  const [focusOverride, setFocusOverride] = useState<{
    base: DocumentTypeFieldState;
    state: DocumentTypeFieldState;
  } | null>(null);
  const focusState =
    activation === 'manual' && focusOverride && focusOverride.base === value
      ? focusOverride.state
      : value;

  function select(next: DocumentTypeFieldState): void {
    if (disabled || busy || next === value) return;
    onChange(next);
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>): void {
    const states = DOCUMENT_TYPE_FIELD_STATE_VALUES;
    const manual = activation === 'manual';
    // W trybie ręcznym ruch liczymy od segmentu z fokusem, w automatycznym od wyboru.
    const index = states.indexOf(manual ? focusState : value);
    let target: DocumentTypeFieldState | undefined;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        target = states[(index + 1) % states.length];
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        target = states[(index - 1 + states.length) % states.length];
        break;
      case 'Home':
        target = states[0];
        break;
      case 'End':
        target = states[states.length - 1];
        break;
      default:
        return;
    }
    event.preventDefault();
    if (!target) return;
    if (manual) {
      setFocusOverride({ base: value, state: target });
      buttons.current[target]?.focus();
      return;
    }
    if (disabled || busy) return;
    select(target);
    buttons.current[target]?.focus();
  }

  return (
    <div
      role="radiogroup"
      aria-label={label}
      aria-disabled={disabled || undefined}
      aria-busy={busy || undefined}
      className="inline-flex flex-none overflow-hidden rounded-lg border border-border-secondary"
    >
      {DOCUMENT_TYPE_FIELD_STATE_VALUES.map((state) => {
        const selected = value === state;
        return (
          <Button
            key={state}
            ref={(node) => {
              buttons.current[state] = node;
            }}
            variant="ghost"
            size="sm"
            role="radio"
            aria-checked={selected}
            aria-disabled={disabled}
            tabIndex={focusState === state ? 0 : -1}
            onClick={() => select(state)}
            onKeyDown={onKeyDown}
            className={cn(
              'h-7 rounded-none px-2.5 text-xs',
              'border-l border-border-secondary first:border-l-0',
              selected
                ? STATE_ACTIVE_CLASS[state]
                : 'bg-bg-primary text-text-secondary hover:bg-bg-tertiary',
              disabled && 'cursor-not-allowed',
              disabled && !selected && 'text-text-muted hover:bg-bg-primary',
            )}
            data-testid={testIdPrefix ? `${testIdPrefix}-${state}` : undefined}
          >
            {t(STATE_LABEL_KEY[state])}
          </Button>
        );
      })}
    </div>
  );
}
